import {useLayoutEffect, useRef, useState} from 'react';
import type {CSSProperties, RefObject} from 'react';
import type {ClientRect, MeasuringConfiguration} from '@dnd-kit/core';
import type {DragLayoutControls} from './layers-panel.types';
import {DRAG_PLACEHOLDER_DATA_ATTRIBUTE} from './layers-panel.types';

/**
 * Keeps the panel from resizing or drifting under the pointer while a drag collapses the list — pins the
 * viewport height, caches the drag-start rect, and scroll-compensates the reflow.
 * Call lockLayout() at drag start and releaseLayout() once it settles; attach listRef to the rows' container.
 */
export const useLockLayoutDuringDrag = (activeDragId: string | null): DragLayoutControls => {
  const listRef = useRef<HTMLDivElement>(null);
  const [lockedBodyHeight, setLockedBodyHeight] = useState<number | null>(null);
  const dragStartRectRef = useRef<ClientRect | null>(null);

  const measureDraggable = (node: HTMLElement): ClientRect => {
    if (dragStartRectRef.current) {
      return dragStartRectRef.current;
    }
    const rect = node.getBoundingClientRect();
    dragStartRectRef.current = rect;
    return rect;
  };

  const measuring: MeasuringConfiguration = {draggable: {measure: measureDraggable}};

  const getScrollViewport = (): HTMLElement | null => listRef.current?.parentElement ?? null;

  useLayoutEffect((): void => {
    if (activeDragId === null) {
      return;
    }
    const dragStartTop = dragStartRectRef.current?.top;
    const placeholder = listRef.current?.querySelector(`[${DRAG_PLACEHOLDER_DATA_ATTRIBUTE}]`);
    const scrollContainer = listRef.current?.parentElement ?? null;
    if (dragStartTop === undefined || !placeholder || !scrollContainer) {
      return;
    }
    scrollContainer.scrollTop += placeholder.getBoundingClientRect().top - dragStartTop;
  }, [activeDragId]);

  const lockLayout = (): void => {
    dragStartRectRef.current = null;
    setLockedBodyHeight(getScrollViewport()?.getBoundingClientRect().height ?? null);
  };

  const releaseLayout = (): void => {
    setLockedBodyHeight(null);
    dragStartRectRef.current = null;
  };

  return {
    listRef,
    getScrollViewport,
    measuring,
    lockedBodyStyle: lockedBodyHeight === null ? undefined : {minHeight: lockedBodyHeight},
    lockLayout,
    releaseLayout,
  };
};

/** Pins the panel's natural height (measured at `auto`) as an explicit value so the CSS `height` transition can animate the popover's grow/shrink. */
export const useAnimatedPanelHeight = (panelRef: RefObject<HTMLDivElement | null>, rowsRef: RefObject<HTMLDivElement | null>, enabled: boolean): CSSProperties | undefined => {
  const [panelHeight, setPanelHeight] = useState<number | null>(null);

  useLayoutEffect((): (() => void) | undefined => {
    const panel = panelRef.current;
    const rows = rowsRef.current;
    if (!enabled || !panel || !rows) {
      setPanelHeight(null);
      return undefined;
    }
    const measure = (): void => {
      const pinnedHeight = panel.style.height;
      panel.style.height = 'auto';
      const naturalHeight = panel.offsetHeight;
      panel.style.height = pinnedHeight;
      setPanelHeight(naturalHeight);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(rows);
    return (): void => {
      observer.disconnect();
    };
  }, [enabled, panelRef, rowsRef]);

  return panelHeight === null ? undefined : {height: panelHeight};
};
